/* iter322 FE — Reusable Controlled Document Upload Modal
 * iter327: + document_code suggestion & override, Admin / SysAuth gating.
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/lib/auth";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Modal, Btn, Input, Select, Textarea } from "@/components/UI";
import { Loader2, Upload, ShieldAlert } from "lucide-react";

const TYPES = [
  { v: "INSTRUCTION", l: "Talimat" },
  { v: "SOP", l: "SOP / Prosedür" },
  { v: "CONTROLLED", l: "Kontrollü Belge (Şema/Politika/Plan/Kılavuz)" },
];

export default function ControlledDocUploadModal({ open, onClose, onCreated }) {
  const { user } = useAuth();
  const canUpload = user?.role === "admin" || user?.system_authority === true;
  const [documentType, setDocumentType] = useState("INSTRUCTION");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [owner, setOwner] = useState("Kalite Yönetim Birimi");
  const [reason, setReason] = useState("İlk yayın");
  const [docCode, setDocCode] = useState("");           // iter327 — editable
  const [suggestedCode, setSuggestedCode] = useState(""); // iter327 — remember suggestion
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [duplicate, setDuplicate] = useState(null);

  // iter327 — fetch next-code suggestion whenever modal opens or type changes.
  useEffect(() => {
    if (!open || !canUpload) return;
    let alive = true;
    api.get(`/gmp/master-documents/suggest-code?document_type=${documentType}`)
      .then(r => {
        if (!alive) return;
        setSuggestedCode(r.suggested_base_code || "");
        setDocCode(r.suggested_base_code || "");
      })
      .catch(() => { if (alive) { setSuggestedCode(""); setDocCode(""); } });
    return () => { alive = false; };
  }, [open, documentType, canUpload]);

  useEffect(() => {
    if (!title || title.length < 2) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/gmp/master-documents/search-titles?q=${encodeURIComponent(title)}`);
        setSuggestions(r || []);
      } catch { setSuggestions([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [title]);

  const reset = () => {
    setTitle(""); setDescription(""); setFile(null);
    setDuplicate(null); setSuggestions([]);
    setDocCode(suggestedCode);
  };

  const submit = async () => {
    if (!title.trim() || !file) { alert("Belge adı ve dosya zorunlu"); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("document_type", documentType);
      form.append("title", title.trim());
      if (description) form.append("description", description);
      if (owner) form.append("owner_department", owner);
      if (reason) form.append("revision_reason", reason);
      // iter327 — only send override if user changed it from the suggestion
      if (docCode.trim() && docCode.trim() !== suggestedCode) {
        form.append("document_code", docCode.trim());
      }
      form.append("file", file);
      const r = await axios.post(`${API}/gmp/master-documents/upload`, form, {
        headers: { "Content-Type": "multipart/form-data",
                    Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
      });
      reset(); onCreated?.(r.data); onClose?.();
    } catch (e) {
      if (e.response?.status === 409) {
        const d = e.response.data?.detail;
        if (typeof d === "string") {
          alert(d);                 // code conflict
        } else {
          setDuplicate(d?.existing || null);
        }
      } else {
        const d = e.response?.data?.detail;
        alert(typeof d === "string" ? d : (d?.message || "Yükleme başarısız"));
      }
    } finally { setBusy(false); }
  };

  const uploadAsRevision = async () => {
    if (!duplicate || !file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("revision_reason", reason || "İçerik güncellemesi");
      form.append("file", file);
      await axios.post(`${API}/gmp/master-documents/${duplicate.id}/upload-revision`, form, {
        headers: { "Content-Type": "multipart/form-data",
                    Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
      });
      reset(); onCreated?.(); onClose?.();
    } catch (e) { alert(e.response?.data?.detail || "Revizyon başarısız"); }
    finally { setBusy(false); }
  };

  // iter327 — normal users can't upload; show polite guard instead of form.
  if (!canUpload) {
    return (
      <Modal open={open} onClose={onClose} title="+ Kontrollü Belge Ekle" size="sm">
        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-300 rounded text-xs text-amber-900"
              data-testid="ctrl-doc-no-permission">
          <ShieldAlert size={16} className="shrink-0 mt-0.5"/>
          <div>
            Kontrollü doküman yüklemek yalnızca <b>Admin</b> veya
            <b> Sistem Yetkilisi</b> rollerine açıktır. Mevcut yetkiniz
            yüklemeye izin vermiyor.
          </div>
        </div>
        <div className="flex justify-end mt-3">
          <Btn variant="secondary" onClick={onClose}>Kapat</Btn>
        </div>
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={onClose} title="+ Kontrollü Belge Ekle" size="md">
      <div className="space-y-3" data-testid="ctrl-doc-upload-modal">
        <Select label="Belge Türü *" value={documentType} onChange={e => setDocumentType(e.target.value)}
                data-testid="ctrl-doc-type">
          {TYPES.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
        </Select>
        <div>
          <Input label="Doküman Kodu" value={docCode}
                  onChange={e => setDocCode(e.target.value)}
                  placeholder={suggestedCode || "T05/KYB"}
                  data-testid="ctrl-doc-code"/>
          <div className="text-[10px] text-slate-500 mt-0.5">
            Öneri: <span className="font-mono">{suggestedCode || "—"}</span>.
            İsterseniz üzerine yazın (örn. <code>T18/KYB</code>). Kayıt
            anında benzersizlik doğrulanır.
          </div>
        </div>
        <div className="relative">
          <Input label="Belge Adı *" value={title} onChange={e => setTitle(e.target.value)}
                  placeholder="Ör. Elektronik Hassas Terazi Kullanım Talimatı"
                  data-testid="ctrl-doc-title"/>
          {suggestions.length > 0 && (
            <div className="absolute z-30 left-0 right-0 top-full mt-0.5 bg-white border rounded shadow-lg max-h-48 overflow-auto">
              {suggestions.map(s => (
                <button key={s.id} onClick={() => setTitle(s.title)}
                        className="w-full text-left px-2 py-1 text-xs hover:bg-indigo-50 flex gap-2 items-center">
                  <span className="font-mono text-[10px] text-slate-500">{s.current_full_code}</span>
                  <span className="flex-1 truncate">{s.title}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <Input label="Açıklama (opsiyonel)" value={description} onChange={e => setDescription(e.target.value)}/>
        <Input label="Sorumlu Birim" value={owner} onChange={e => setOwner(e.target.value)}/>
        <Input label="Revizyon Nedeni" value={reason} onChange={e => setReason(e.target.value)}/>
        <div>
          <label className="text-xs font-semibold text-slate-600 block mb-1">Dosya (PDF/DOCX/Image) *</label>
          <input type="file" onChange={e => setFile(e.target.files?.[0] || null)}
                  accept=".pdf,.doc,.docx,image/*"
                  className="text-xs border rounded px-2 py-1 w-full"
                  data-testid="ctrl-doc-file"/>
          {file && <div className="text-[10px] text-slate-500 mt-0.5">{file.name} · {Math.round(file.size/1024)} KB</div>}
        </div>

        {duplicate && (
          <div className="bg-amber-50 border border-amber-300 rounded p-2 text-xs text-amber-900" data-testid="ctrl-doc-dup">
            <div className="font-semibold">⚠ Bu isimde mevcut bir kontrollü doküman bulunuyor:</div>
            <div className="font-mono text-[11px]">{duplicate.current_full_code} — {duplicate.title}</div>
            <div className="mt-2 flex gap-2">
              <Btn size="sm" onClick={uploadAsRevision} disabled={busy || !file} data-testid="ctrl-doc-upload-rev">
                {busy ? <Loader2 size={11} className="animate-spin inline"/> : null} Mevcut Belgeye Yeni Revizyon Yükle
              </Btn>
              <Btn size="sm" variant="secondary" onClick={() => setDuplicate(null)} data-testid="ctrl-doc-dup-cancel">Vazgeç</Btn>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t">
          <Btn variant="secondary" onClick={onClose}>Vazgeç</Btn>
          <Btn onClick={submit} disabled={busy || !title.trim() || !file || !!duplicate} data-testid="ctrl-doc-submit">
            {busy ? <><Loader2 size={11} className="animate-spin inline mr-1"/>Yükleniyor…</> : <><Upload size={11} className="inline mr-1"/>Yükle</>}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

/* Revision-only mode: existing doc, upload a new revision */
export function RevisionUploadModal({ open, onClose, doc, onCreated }) {
  const [reason, setReason] = useState("İçerik güncellemesi");
  const [changeSummary, setChangeSummary] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!file || !reason.trim()) { alert("Dosya ve neden zorunlu"); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("revision_reason", reason);
      if (changeSummary) form.append("change_summary", changeSummary);
      form.append("file", file);
      await axios.post(`${API}/gmp/master-documents/${doc.id}/upload-revision`, form, {
        headers: { "Content-Type": "multipart/form-data",
                    Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
      });
      onCreated?.(); onClose?.();
    } catch (e) { alert(e.response?.data?.detail || "Revizyon başarısız"); }
    finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title={`Yeni Revizyon · ${doc?.current_full_code || ""}`} size="md">
      <div className="space-y-3" data-testid="rev-upload-modal">
        <div className="bg-slate-50 border rounded p-2 text-xs">
          <div className="font-semibold">{doc?.title}</div>
          <div className="text-slate-500">Mevcut: Rev.{String(doc?.current_revision_no || 0).padStart(2, "0")} · Yeni: Rev.{String((doc?.current_revision_no || 0) + 1).padStart(2, "0")}</div>
        </div>
        <Input label="Revizyon Nedeni *" value={reason} onChange={e => setReason(e.target.value)} data-testid="rev-reason"/>
        <Textarea label="Değişiklik Özeti" rows={2} value={changeSummary} onChange={e => setChangeSummary(e.target.value)}/>
        <div>
          <label className="text-xs font-semibold text-slate-600 block mb-1">Yeni Revizyon Dosyası *</label>
          <input type="file" onChange={e => setFile(e.target.files?.[0] || null)}
                  accept=".pdf,.doc,.docx,image/*" className="text-xs border rounded px-2 py-1 w-full"
                  data-testid="rev-file"/>
          {file && <div className="text-[10px] text-slate-500 mt-0.5">{file.name}</div>}
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t">
          <Btn variant="secondary" onClick={onClose}>Vazgeç</Btn>
          <Btn onClick={submit} disabled={busy || !file} data-testid="rev-submit">
            {busy ? <Loader2 size={11} className="animate-spin inline"/> : <Upload size={11}/>} Yükle
          </Btn>
        </div>
      </div>
    </Modal>
  );
}
